/**
 * "This project was split into structures" announcement on the Files tab.
 *
 * Each CRM user sees the announcement once per split: we remember (per user,
 * per project, in localStorage) the timestamp of the newest split they have
 * acknowledged, and show the banner whenever a newer split exists. Pure
 * helpers here; the component decides when to call `markSplitSeen`.
 */

import type { SplitHistoryEvent } from './projectFileSplitHistory';

export function splitSeenStorageKey(projectId: string, uid: string): string {
  return `lctf:filesSplitSeen:${projectId}:${uid}`;
}

/** Timestamp (ms) of the newest split this user has acknowledged; 0 when none. */
export function readSplitSeenAt(projectId: string, uid: string, storage: Storage | null = safeStorage()): number {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(splitSeenStorageKey(projectId, uid));
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeSplitSeenAt(projectId: string, uid: string, at: number, storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(splitSeenStorageKey(projectId, uid), String(at));
  } catch {
    /* private mode / quota — the banner just shows again next visit */
  }
}

/**
 * The split to announce: the newest one that happened after the user last
 * acknowledged a split on this project. A group added after the split is not
 * announced — the tracks were already split. `null` when there is nothing new.
 */
export function pendingSplitAnnouncement(events: SplitHistoryEvent[], seenAt: number): SplitHistoryEvent | null {
  let latest: SplitHistoryEvent | null = null;
  for (const ev of events) {
    if (ev.addedAfterSplit) continue;
    const t = ev.at.getTime();
    if (!Number.isFinite(t) || t <= seenAt) continue;
    if (!latest || t > latest.at.getTime()) latest = ev;
  }
  return latest;
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
